// src/pages/contentManagement/streaming/livestreamConfig.js
import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, Switch } from "antd";
import { FaVideo, FaCheckCircle, FaTimesCircle, FaHistory } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { ADMIN_ROLES, MEDIA_ROLES, getStoredRoleNormalized } from "../../../auth/permissions";

// ─── Constants ─────────────────────────────────────────────────────────────────
// Admins and media roles can toggle livestream detection.
const LIVESTREAM_WRITE_ROLES = [...ADMIN_ROLES, ...MEDIA_ROLES];

function LivestreamConfig({ youtube, isCredentialsConfigured, onSaveSuccess, onBackfillStarted, showNotification }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  const role = getStoredRoleNormalized();
  const canWrite = LIVESTREAM_WRITE_ROLES.includes(role);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isEnabled = youtube?.livestream?.isEnabled || false;
  const channelId = youtube?.channelId || "";
  const isBackfilling = youtube?.isBackfilling || false;
  const backfillDone = youtube?.backfillCompleted || false;

  // ── Reset isPolling once backfill is confirmed complete ────────────────────
  useEffect(() => {
    if (isPolling && backfillDone) {
      setIsPolling(false);
    }
  }, [backfillDone, isPolling]);

  // ── Import YouTube history ─────────────────────────────────────────────────
  const handleBackfill = useCallback(async () => {
    try {
      const token = await getAuthToken();
      await axios.post(API_ENDPOINTS.YOUTUBE_SETTINGS_BACKFILL, {}, { headers: { Authorization: `Bearer ${token}` } });
      showNotification("YouTube history import started. This may take a moment.", "success", "Import Started");
      setIsPolling(true);
      onBackfillStarted();
    } catch (error) {
      console.error("Backfill failed:", error);
      showNotification(error.response?.data?.message || "Failed to start import. Please try again.", "error", "Import Failed");
    }
  }, [onBackfillStarted, showNotification]);

  // ── Toggle livestream detection ────────────────────────────────────────────
  const handleToggle = useCallback(
    async (checked) => {
      setIsSubmitting(true);
      try {
        const token = await getAuthToken();
        const response = await axios.patch(
          API_ENDPOINTS.YOUTUBE_SETTINGS_LIVESTREAMS,
          { isEnabled: checked },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification(`Livestream detection ${checked ? "enabled" : "disabled"} successfully.`, "success");
        onSaveSuccess(response.data);
      } catch (error) {
        console.error("Error toggling livestream:", error);
        showNotification(error.response?.data?.message || "Failed to update livestream setting. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSaveSuccess, showNotification]
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="strm-upload-section">
      {/* ── Section header ───────────────────────────────────────────────── */}
      <div className="strm-section-header">
        <FaVideo className="strm-section-icon" />
        <div>
          <h3 className="strm-section-title">Livestream Configuration</h3>
          <p className="strm-section-subtitle">
            Import your church's YouTube livestream history for the mobile app, and configure automatic live stream detection so members are
            notified when you go live.
          </p>
        </div>
      </div>

      {/* ── Import YouTube History ────────────────────────────────────────── */}
      {isCredentialsConfigured && (
        <Form.Item>
          <Button
            icon={<FaHistory />}
            onClick={handleBackfill}
            loading={isBackfilling || isPolling}
            disabled={backfillDone || isBackfilling || isPolling}
          >
            {backfillDone ? "YouTube History Imported" : "Import YouTube History"}
          </Button>
        </Form.Item>
      )}

      {/* ── How it works ───────────────────────────────────────────────────
      <div className="strm-info-block">
        <FaInfoCircle className="strm-info-icon" />
        <p className="strm-info-text">
          Livestream detection uses your YouTube channel ID to automatically check for active streams on a scheduled basis. No playlist ID
          is needed — your channel is monitored directly.
        </p>
      </div> */}

      {/* ── Credentials gate ─────────────────────────────────────────────── */}
      {!isCredentialsConfigured && (
        <div className="strm-gate-notice">YouTube channel credentials must be configured before livestream detection can be enabled.</div>
      )}

      {/* ── Toggle row ───────────────────────────────────────────────────── */}
      <div className="strm-livestream-toggle-row">
        <div className="strm-livestream-toggle-info">
          <span className="strm-livestream-toggle-label">Livestream Detection</span>

          {/* Status badge */}
          {isCredentialsConfigured && (
            <span className={`strm-status-badge ${isEnabled ? "strm-status-badge--configured" : "strm-status-badge--missing"}`}>
              {isEnabled ? (
                <>
                  <FaCheckCircle />
                  Active — monitoring {channelId}
                </>
              ) : (
                <>
                  <FaTimesCircle />
                  Inactive
                </>
              )}
            </span>
          )}
        </div>

        <Switch
          checked={isEnabled}
          onChange={handleToggle}
          loading={isSubmitting}
          disabled={!canWrite || !isCredentialsConfigured || isSubmitting}
        />
      </div>
    </div>
  );
}

export default LivestreamConfig;
